<template>
  <div class="card-wrapper" :class="zoomSelection">
    <card-header v-if="showHeader" :enable-suggestions="false" :page-number="1" :card-type="'kp-card'" />
    <div class="card-content">
      <div class="unhide-text" v-if="isHide">
        <button type="button" class="btn btn-md btn-primary" @click="$emit('showCard', cardMeta.id)">
          Unhide
        </button>
      </div>
      <div class="unhide-overlay" v-if="isHide" />
      <div class="spinner-border" v-if="isLoading"></div>
      <iframe :srcdoc="htmlContent" scrolling="no" frameborder="no" :id="cardMeta.id"></iframe>
      <!-- DM-11385: the AI Overview's sources, inside the captured panel (see mountAioSources) -->
      <Teleport v-if="aioMount" :to="aioMount">
        <kp-aio-sources :sources="aioverview?.urls || []" :compact="compactAio" :ts="aioTs" :keyword="cardMeta.keyword" />
      </Teleport>
    </div>
  </div>
</template>

<script>
import { computed, markRaw } from "vue";
import CardHeader from "./card/CardHeader.vue";
import KpAioSources from "./card/KpAioSources.vue";
import { mapGetters } from "vuex";
import HighlightUtil from '../utils/highlightUtil';

// DM-9664 Some captures leave #rhs an empty spacer and carry the knowledge panel in the main
// column instead, as separate sections Google has collapsed to height 0 - Overview with its
// facts table, Listen, People also search for. Every measurement in this component keys off one
// wrapper element, so with nothing to key off the card came up blank. assembleWholepagePanel
// gathers those sections into one element under this id, and the rest of the component then
// treats it as the panel.
//
// The sections are matched on .z4oRIf, which this component already treats as the panel's own
// marker (see the heightElement line and showHiddenElementsRepeatedly below). Deliberately NOT
// .kp-wholepage-osrp: that also matches the Members / Songs / Albums carousels and the image
// row, none of which are the knowledge panel. The image row in particular is the Featured
// results panel, which is a separate result we capture separately.
const ASSEMBLED_PANEL_ID = 'fb-assembled-kp';
const WHOLEPAGE_SECTION_SELECTOR = '.z4oRIf';
const PANEL_PADDING = 8;
const RHS_PANEL_SELECTOR = '.kp-wholepage, .kp-wholepage-osrp, div[role="complementary"], [data-fb-rrhs]';

export default {
  name: "KPCard",
  emits: ["showCard", "hoverFocus", "highlight", "resize"],
  components: {
    CardHeader,
    KpAioSources,
  },
  provide() {
    return {
      cardMeta: computed(() => this.cardMeta)
    }
  },
  props: {
    cardMeta: Object,
    termData: Object,
    isHide: Boolean,
    hoverFocusItem: Object,
    shouldZoom: {
      type: Boolean,
      required: false,
      default: true,
    },
    isLinksActive: {
      type: Boolean,
      required: true,
    },
    // DM-11385: the panel's AI Overview ({ text, urls }) from the timeline data, with each
    // source's markup; its sources are listed under the AI Overview text inside the panel.
    aioverview: {
      type: Object,
      default: null,
    },
    // the Knowledge Panel view's one-line source rows
    compactAio: {
      type: Boolean,
      default: false,
    },
    // a panel without an AI Overview says so (the Knowledge Panel view, when another day has one)
    showNoAio: {
      type: Boolean,
      default: false,
    },
    // the card's date, for the Markup dialog
    aioTs: {
      type: Number,
      default: 0,
    },
    // the KP popup: the panel takes the popup's whole width (fillCardBox)
    fillWidth: {
      type: Boolean,
      default: false,
    },
    // the KP popup has its own header
    showHeader: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      zoom: 1,
      bufferWidth: 10,
      bufferHeight: 20,
      htmlContent: "",
      isLoading: true,
      // When auto-calculated width is too large (no proper KP structure), use fallback width
      // This prevents KP from expanding to full page width when Google's layout constraints fail
      maxAutoWidth: 700,
      fallbackKpWidth: 400,
      // An iframe no wider than this never got a real measurement - see resizeWhenVisible.
      collapsedWidth: 10,
      reloadAttempts: 0,
      maxReloadAttempts: 3,
      // Content shorter than this is an icon or a spacer, not a picture worth clamping,
      // and a couple of pixels over its box is rounding rather than overflow.
      minClampHeight: 24,
      clampTolerance: 4,
      // Observers watching the iframe document, kept so they can be stopped with the
      // card - they walk a multi-MB DOM on every mutation, so leaving them running
      // after the card is gone costs real time on a timeline of KPs.
      observers: [],
      visibilityObserver: null,
      // Kept out of observers[]: trackCardHeight() re-runs when termData changes and has to
      // disconnect its own previous observer, which is still watching an element of the old
      // srcdoc document and would keep sizing the new card from it.
      cardHeightObserver: null,
      // Elements the removals pass hid on purpose. revealKpColumn walks ancestors and
      // would otherwise re-show one with an !important it could never take back - the
      // top:-0.05px sweep in particular matches arbitrary elements.
      hiddenByUs: new Set(),
      // the element inside the iframe the AI Overview's sources render into
      aioMount: null,
    };
  },
  computed: {
    ...mapGetters("highlight", {
      highlightedKP: "highlightedKP",
    }),
    ...mapGetters('toolbar', { zoomSelection: 'zoomSelection' }),
    ...mapGetters("routeParams", {
      resultset: "resultset",
    }),

    loadingHtmlComplete() {
      return this.htmlContent === "";
    }
  },

  methods: {
    /*
     * These resolve the live frame and its document. Methods, not computed: a srcdoc
     * re-navigation replaces contentWindow.document with a new object, and a cached
     * computed would keep handing back the dead one.
     */
    iframe() {
      return document.getElementById(this.cardMeta.id);
    },
    iframeDocument() {
      return this.iframe().contentWindow.document;
    },
    iframeStyle() {
      return getComputedStyle(this.iframe());
    },
    /*
     * The element that clips the iframe down to the card. The iframe itself is rendered much
     * wider than the card on purpose - see the stylesheet at the bottom of this file.
     */
    cardBox() {
      const frame = this.iframe();
      return frame ? frame.parentElement : null;
    },

    /*
     * cardWidth, kpElements and kpWrapper measure the live iframe DOM, which Vue cannot
     * track, so they are methods rather than computed. As computed they cached against
     * reactive deps that never change: a measurement taken while the card was hidden -
     * where everything reads 0 - was then handed back forever and the card could never
     * recover its real size.
     *
     * determines the width of the card
     */
    cardWidth() {
      // A panel we assembled was already built to the card's width, so measure the panel. Going
      // through a section instead subtracts its padding and its scrollbar a second time, and the
      // card ends up narrower than the panel it is meant to show.
      const assembled = this.iframeDocument().getElementById(ASSEMBLED_PANEL_ID);
      if (assembled) {
        return assembled.offsetWidth * this.zoom;
      }

      let width = 0;
      const kp = this.kpElements();

      if (kp && kp.length) {
        width = (kp[0].clientWidth + this.bufferWidth) * this.zoom;
      }

      // Apply fallback width if needed (see data() for explanation)
      if (width > this.maxAutoWidth) {
        width = this.fallbackKpWidth + this.bufferWidth;
      }
      return width;
    },

    kpElements() {
      // A panel we assembled is made of sections that match none of the selectors below, so
      // without this the search falls through to the whole document and sizes the card from
      // something that is not the panel.
      const assembled = this.iframeDocument().getElementById(ASSEMBLED_PANEL_ID);
      if (assembled) {
        return assembled.children;
      }

      let startElement = this.kpWrapper() ? this.kpWrapper() : this.iframeDocument();

      let kp = startElement.querySelectorAll('.kp-wholepage, .kp-wholepage-osrp, [data-fb-rrhs]');

      if (!kp.length && startElement.matches?.('.kp-wholepage, .kp-wholepage-osrp, [data-fb-rrhs]')) {
        kp = [startElement];
      }

      if (!kp.length && !startElement.querySelectorAll('div[role="complementary"]').length &&
        !startElement.matches?.('[role="complementary"]')) {
        kp = this.iframeDocument().querySelectorAll('.kp-wholepage, .kp-wholepage-osrp, [data-fb-rrhs]');
      }

      if (!kp.length) {
        kp = startElement.querySelectorAll('div[role="complementary"]');
      }

      if (!kp.length) {
        kp = startElement.querySelectorAll('.knowledge-panel');
      }

      if (!kp.length) {
        kp = startElement.querySelectorAll('div[jscontroller="nPaQu"]');
      }

      // finds the See results about element when there is no actual KP
      if (!kp.length) {
        kp = startElement.querySelectorAll('block-component div');
      }
      return kp;
    },

    /*
     * determines the height of the card, and what element to position in the corner of the iframe
     */
    kpWrapper() {
      // A wholepage panel we gathered ourselves (see ASSEMBLED_PANEL_ID) is the panel.
      const assembled = this.iframeDocument().getElementById(ASSEMBLED_PANEL_ID);
      if (assembled) {
        return assembled;
      }

      // DM-5314 google introduces a selector 'aria-owns'. Its value is the id of another element. The element whose id it is is effectively moved to ebing a child of the aria-owns element
      // even though in the DOM, it is completely unrelated. Sometimes #rhs, which is technically the parent of the kp, is moved to a different part of the DOM
      // and is unlinked from the kp hierarchy. Therefore, I decided to check to see if the kp is a child of #rhs, if it is, then proceed as normal. If it isn't, reevaluate.
      let kp = this.iframeDocument().querySelector('#rhs');

      // see above about data-fb-rrhs - changed by us from data-rrhs
      if (!kp || (!kp.querySelector('.kp-wholepage, .kp-wholepage-osrp, div[role="complementary"], [data-fb-rrhs]') && !kp.matches('[role="complementary"]'))) {
        // look for the first element that is not in the center column
        const dataItems = this.iframeDocument().querySelectorAll('.kp-wholepage, .kp-wholepage-osrp, div[role="complementary"], [data-fb-rrhs]');
        // TODO: make sure the one we take has a min height of 100px
        kp = Array.from(dataItems).find(item => !item.closest('#center_col') && item.clientHeight >= 200);
      }

      if (!kp) {
        kp = this.iframeDocument().querySelector('.knowledge-panel');
      }

      if (!kp) {
        kp = this.iframeDocument().querySelector('#rso .kp-blk');
      }
      return kp;
    },

    processMobile() {
      const style = document.createElement("style");
      style.textContent = `
      div {
        max-width: ${this.cardWidth()}px;
      }
      `;
      this.iframeDocument().head.append(style);
    },

    processKpHtml() {
      if (!this.termData.html) {
        return;
      }
      this.htmlContent = this.termData.html;
      // DM-5609 - When the page is narrow, google sometimes has JS that removes these elements from the rhs. This effectively disables that JS because it can't find the elements anymore.
      this.htmlContent = this.htmlContent.replace(/data-rrhs=/g, 'data-fb-rrhs=');

      this.iframe().onload = () => {
        // The srcdoc may still be loading when the card is unmounted (TimelineKP drops
        // cards on a resolution change), and a reload would otherwise stack a second set
        // of observers on top of the ones watching the old document.
        if (!this.iframe()) {
          return;
        }
        this.observers.splice(0).forEach(observer => observer.disconnect());
        // Same for the height observer: it is watching an element of the document this load
        // replaces, and would keep sizing the new card from the old element's scrollHeight.
        if (this.cardHeightObserver) {
          this.cardHeightObserver.disconnect();
          this.cardHeightObserver = null;
        }
        // Set up the watcher first: this load may have carried the empty document, and
        // the watcher is what re-applies srcdoc to get the real one.
        this.resizeWhenVisible();

        // Vue renders the iframe with srcdoc="" and patches the real HTML in a tick later,
        // so on some machines the empty initial document fires its own load event first.
        // Processing that one prepends #box into a blank document and measures nothing,
        // leaving the card stuck at its unsized 400x150 with the snapshot sitting unused in
        // the srcdoc attribute - a blank KP popup on one machine and not another.
        if (this.isFrameBlank()) {
          return;
        }

        if (this.termData && this.termData.hasKP) {

          const body = this.iframeDocument().body;

          // DM-9664 Gather the panel before anything measures or hides: on a wholepage capture
          // the loose sections ARE the panel, and until they are gathered kpWrapper() has
          // nothing to find.
          this.assembleWholepagePanel();

          // Pin before the removals below, so they can tell what the KP is and leave its
          // ancestors alone, and before processMobile, which sizes itself off cardWidth().
          // Take the wrapper layoutKp actually pinned. Asking kpWrapper() again can answer
          // differently - its fallback branch selects on clientHeight >= 200, and layoutKp
          // just changed the layout - and the removals guard below would then be protecting
          // an element that was never pinned.
          const kp = this.layoutKp();

          if (this.resultset.includes('mobile')) {
            this.processMobile(body);
          }

          const aLinks = body.querySelectorAll('a');
          aLinks.forEach(aLink => {
            if (aLink) {
              if (this.isLinksActive) {
                aLink.setAttribute('target', '_blank');
              } else {
                aLink.style['pointer-events'] = "none";
                aLink.style.cursor = "default";
              }
            }
          });

          // Items for removal 
          const removals = [];
          removals.push(body.querySelector('div[role="dialog"]'));
          removals.push(body.querySelector('#searchform'));
          //removes adds from top
          removals.push(body.querySelector('.cu-container'));
          removals.push(body.querySelector('#loc_date'));
          const elements = body.querySelectorAll('*');
          elements.forEach(el => {
            if (el.style.top === '-0.05px') {
              removals.push(el);
            }
          })          

          this.mountAioSources(kp);

          removals.forEach(removal => {
            // DM-10278 The KP is revealed and pinned above, so skip anything that would hide it
            // again - Node.contains() is true for the element itself and its ancestors. The
            // top:-0.05px sweep is the one to watch: it matches arbitrary elements, an ancestor
            // of the KP among them, and hiddenByUs would then stop revealKpColumn undoing it.
            if (removal && !(kp && removal.contains(kp))) {
              removal.style.display = "none";
              this.hiddenByUs.add(removal);
            }
          });
        }
        const heightElement = this.iframeDocument().querySelector('.z4oRIf');
        if (heightElement) {
          heightElement.style.height = '100%';
        }

        if (this.termData.hasKP) {
          this.addBackground();
          this.forceElementsVisible();
          this.clampOverflowingImages();
        }
        this.removeClassRepeatedly(this.iframe().contentDocument);
        this.showHiddenElementsRepeatedly(this.iframe().contentDocument);
        this.initZoom();

        // Recalculate height after forcing elements visible. Measuring once is not enough: the
        // panel keeps growing after load as its images arrive and as the observers above unhide
        // collapsed sections, and a height measured too early leaves the tail of the panel
        // outside the iframe, where no amount of scrolling reaches it.
        if (this.termData.hasKP) {
          setTimeout(() => {
            this.clampOverflowingImages();
            this.initCardHeight();
            this.trackCardHeight();
          }, 1000);
        }

        // Set event listeners
        const dataAttr = this.iframeDocument().querySelectorAll("[data-attrid*=':/']");
        if (dataAttr.length) {
          dataAttr.forEach((node) => {
            node.style.border = "1px solid transparent";

            node.addEventListener("mouseenter", (e) => {
              const emitValue = e.srcElement.getAttribute("data-attrid");
              this.$emit("hoverFocus", {
                value: emitValue,
                isHoverFocus: true,
              });
            });

            node.addEventListener("mouseleave", (e) => {
              const emitValue = e.srcElement.getAttribute("data-attrid");
              this.$emit("hoverFocus", {
                value: emitValue,
                isHoverFocus: false,
              });
            });

            node.addEventListener("click", () => {
              this.$emit('highlight', this.cardMeta.id);
            });
          });
        }
      };
    },

    /*
     * DM-11385: where KpAioSources renders inside the captured panel - right after the AI
     * Overview's answer (`[data-subtree="aimc"]`), whose own source cards it replaces (Google
     * shows one card and hides the rest behind "+N" chips; the list shows every source). A panel
     * without an AI Overview gets the "No AI Overview" line under its title when showNoAio is set.
     * The element is created per document load; the list re-renders from the props.
     */
    mountAioSources(kp) {
      this.aioMount = null;
      this.iframeDocument().querySelectorAll('[data-fb-kp-aio]').forEach((el) => el.remove());
      const hasAio = !!this.aioverview?.urls?.length;
      // The "No AI Overview" line only where the capture shows none either: a day parsed before
      // the sources were stored still shows Google's AI Overview.
      if (!kp || (!hasAio && (!this.showNoAio || kp.querySelector('[data-subtree="aimc"]')))) {
        return;
      }
      const doc = this.iframeDocument();
      const mount = doc.createElement('div');
      mount.setAttribute('data-fb-kp-aio', '');
      const answer = hasAio && kp.querySelector('[data-subtree="aimc"]');
      if (answer) {
        answer.querySelectorAll('[role="list"]').forEach((list) => {
          if (list.querySelector('[role="listitem"] a[href]')) {
            list.style.setProperty('display', 'none', 'important');
          }
        });
        // Google shows the answer collapsed behind "Show more", which needs its scripts (stripped
        // from the capture): open every box that clips it, drop the button, and put the list
        // after the outermost box.
        const clipping = this.clippingAncestors(answer, kp);
        clipping.forEach((box) => {
          box.style.setProperty('height', 'auto', 'important');
          box.style.setProperty('max-height', 'none', 'important');
          box.style.setProperty('overflow', 'visible', 'important');
        });
        kp.querySelectorAll('[role="button"][aria-expanded="false"][aria-controls]').forEach((button) => {
          if (doc.getElementById(button.getAttribute('aria-controls'))?.contains(answer)) {
            button.style.setProperty('display', 'none', 'important');
          }
        });
        (clipping[clipping.length - 1] || answer).after(mount);
      } else {
        const heading = kp.querySelector('[data-attrid="subtitle"]') || kp.querySelector('[data-attrid="title"]');
        heading ? heading.after(mount) : kp.prepend(mount);
      }
      // A clicked row opens the Markup dialog; the panel's own click handlers stay out of it.
      mount.addEventListener('click', (e) => e.stopPropagation());
      this.aioMount = markRaw(mount);
      this.fitAioSources();
    },
    // The Knowledge Panel view loads the AI Overview data next to the captured page, so it can
    // arrive after the page did: mount (again) into the page that is already there.
    remountAioSources() {
      if (this.iframe() && !this.isFrameBlank() && this.termData?.hasKP) {
        this.mountAioSources(this.kpWrapper());
      }
    },
    // The ancestors of `el` (inside `kp`) that hide part of their content, innermost first.
    clippingAncestors(el, kp) {
      const win = this.iframe().contentWindow;
      const clipping = [];
      for (let a = el.parentElement; a && a !== kp; a = a.parentElement) {
        if (/hidden|clip/.test(win.getComputedStyle(a).overflowY) && a.scrollHeight - a.clientHeight > this.clampTolerance) {
          clipping.push(a);
        }
      }
      return clipping;
    },

    /*
     * Pins the KP into the corner of the iframe. Idempotent, and deliberately separate from
     * the onload handler: which element kpWrapper picks depends on measured heights, so a
     * pass that ran while the card was hidden can pick the wrong element - or none - and a
     * later retry has to be able to redo this, not just the sizing.
     */
    layoutKp() {
      // Must run before anything measures the KP: a hidden column reports 0x0, which would
      // size the card to nothing.
      this.revealKpColumn();

      const kp = this.kpWrapper();
      if (!kp) {
        return null;
      }

      // A panel we assembled is ours and was sized to the card when it was built. A panel
      // google laid out has to be measured instead, below.
      const isAssembled = kp.id === ASSEMBLED_PANEL_ID;

      // Undo what an earlier pass did before measuring. Master's resizeWhenVisible can call
      // this a second time, and clientWidth on an already-pinned, already-padded box hands
      // back the width that pass set - so the retry would only ever re-confirm its own
      // fallback, and the padding would be counted again on every pass. Same trick
      // setCardWidth and initCardHeight each use before they measure.
      if (!isAssembled) {
        ['position', 'width', 'padding-left', 'padding-right']
          .forEach((prop) => kp.style.removeProperty(prop));
      }

      // DM-10278 Measure BEFORE going position:fixed. Out of flow with `width: auto` the panel
      // shrink-to-fits against the iframe's own width, which is deliberately much wider than
      // the card (see --timeline-card-render-width-kp) - it would stretch to that width and the
      // card would clip most of it away. In flow it still has the width Google's layout gave
      // it, which is the width to keep. Revealing first is what makes this measurable on a
      // DM-10278 capture: at the 1250px render width a revealed #rhs takes its real 372px grid
      // column, so it goes through this path rather than the fallback below.
      const naturalWidth = isAssembled ? 0 : kp.clientWidth;

      kp.style.position = 'fixed';
      kp.style.top = 0;
      kp.style.left = 0;
      kp.style['z-index'] = '99999';
      kp.style.background = 'white';
      kp.style['padding-left'] = this.bufferWidth / 2 + 'px';
      kp.style['padding-right'] = this.bufferWidth / 2 + 'px';
      kp.style['margin-left'] = 0;
      kp.style['margin-right'] = 0;
      // Never for a panel we assembled: assembleWholepagePanel already sized it to the card,
      // and kp.clientWidth includes the padding this would then re-add, so the card grew
      // PANEL_PADDING*2 on every reload.
      //
      // The fallback is 100%, not inherit: the wrapper is position:fixed, and `inherit` copies
      // the parent's computed width - which is `auto` - so the wrapper falls back to
      // shrink-to-fit, and on a snapshot whose content has no intrinsic width that collapses to
      // a few pixels, taking the whole card down with it. 100% of the wide frame is far too
      // wide, but the clamp below then puts it at fallbackKpWidth, which is a real card.
      if (!isAssembled) {
        kp.style.width = naturalWidth ? naturalWidth + 'px' : '100%';
      }

      // DM-10278 cardWidth() measures kpElements()[0], a descendant of the wrapper, which
      // google's responsive rules can hide independently of the wrapper itself.
      const measuredElement = this.kpElements()[0];
      if (measuredElement) {
        this.unhideThroughAncestors(measuredElement);
      }

      // Apply fallback width if needed (see data() for explanation)
      setTimeout(() => {
        const computedWidth = kp.offsetWidth;
        if (computedWidth > this.maxAutoWidth) {
          console.debug(`KP width too wide (${computedWidth}px > ${this.maxAutoWidth}px), setting to fallback width ${this.fallbackKpWidth}px`);
          kp.style.width = this.fallbackKpWidth + 'px';
        }
      }, 100);

      return kp;
    },

    // See ASSEMBLED_PANEL_ID. Returns the gathered panel, or null when the page uses the
    // right-rail layout and the existing #rhs path already has a wrapper to work with.
    assembleWholepagePanel() {
      const doc = this.iframeDocument();

      const existing = doc.getElementById(ASSEMBLED_PANEL_ID);
      if (existing) {
        return existing;
      }

      // A populated #rhs is the normal path's panel, so leave it alone. Height alone cannot
      // decide that: DM-10278 captures hide #rhs outright (`.rWfmBc .YNk70c .vVVcqf`) or collapse
      // it to 0 width in the #rcnt grid, so it measures 0 tall however much it holds, and we
      // would assemble a panel out of the sections revealKpColumn is about to show. Fall back to
      // asking whether it has section content, which does not depend on it being laid out.
      const rhs = doc.querySelector('#rhs');
      const rhsHoldsPanel = rhs && rhs.querySelector(RHS_PANEL_SELECTOR);
      const rhsHasContent = rhs && Array.from(rhs.querySelectorAll(WHOLEPAGE_SECTION_SELECTOR))
        .some((el) => (el.innerText || '').trim());
      if (rhsHoldsPanel && (rhs.clientHeight >= 200 || rhsHasContent)) {
        return null;
      }

      const sections = [];
      const seen = new Set();
      doc.querySelectorAll(WHOLEPAGE_SECTION_SELECTOR).forEach((el) => {
        // A section nested inside one we already have is part of it, not a section of its own.
        if (el.parentElement && el.parentElement.closest(WHOLEPAGE_SECTION_SELECTOR)) {
          return;
        }
        const text = (el.innerText || '').replace(/\s+/g, ' ').trim();
        if (!text) {
          return;
        }
        // Google serves some sections twice - once laid out, once collapsed to height 0 - so
        // match on the text to keep only the first of each.
        const key = text.slice(0, 60);
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        sections.push(el);
      });

      if (sections.length < 2) {
        return null;
      }

      // These sections reflow, so give them the card's width rather than the width they happen
      // to have in the results column - that is the column's width, not the panel's, and it
      // would make the card twice as wide as a knowledge panel card anywhere else in the app.
      const cardBox = this.cardBox();
      const width = (cardBox && cardBox.clientWidth)
        ? cardBox.clientWidth - PANEL_PADDING * 2
        : sections[0].clientWidth;

      // A section collapsed to height 0 is placed by script we are not running, so it carries no
      // usable position - keep those after the ones that laid themselves out, in document order.
      const laidOut = sections.filter((s) => s.clientHeight > 0);
      const collapsed = sections.filter((s) => s.clientHeight === 0);

      const panel = doc.createElement('div');
      panel.id = ASSEMBLED_PANEL_ID;
      panel.style.background = 'white';
      panel.style.width = width + 'px';
      panel.style.padding = PANEL_PADDING + 'px';

      laidOut.concat(collapsed).forEach((section) => {
        section.style.width = width + 'px';
        section.style.height = 'auto';
        section.style.margin = '0 0 10px 0';
        // A row that turns out not to reflow - a strip of thumbnails, say - scrolls rather than
        // losing its right-hand half to the card's edge. Set unconditionally rather than on a
        // measurement: nothing is at its final width yet, because the images have not loaded. A
        // row that does fit gets no scrollbar from this.
        section.style.overflowX = 'auto';
        // overflow-x:auto forces overflow-y from visible to auto, so Google's negative bottom
        // margins (the Feedback row's .I0pmUc{margin-bottom:-16px}) would grow a vertical
        // scrollbar on the section. Clip instead of scroll - the 16px it hides is only that
        // dead Feedback button.
        section.style.overflowY = 'hidden';
        panel.appendChild(section);
      });

      doc.body.prepend(panel);
      return panel;
    },

    /*
     * Google sizes KP gallery images with height:100%, which only resolves once its own
     * scripts have set definite heights on their containers. The poller strips scripts, so
     * the images fall back to their natural size, overrun the box the layout reserved for
     * them, and paint over the text underneath. The saved markup is fine - what is missing
     * is the runtime layout, and nothing here can recreate it. So contain the overflow
     * instead: scale an image down to the box it escaped, and clip a gallery that still
     * overruns as a whole. Both only fire where content genuinely exceeds a shorter
     * ancestor, so a KP that already lays out correctly is left untouched.
     */
    clampOverflowingImages() {
      const doc = this.iframeDocument();
      const win = this.iframe().contentWindow;
      const heightOf = el => el.getBoundingClientRect().height;

      // the nearest ancestor shorter than the content - the box it was meant to sit in
      const boxEscapedBy = (el, height) => {
        for (let ancestor = el.parentElement; ancestor && ancestor !== doc.body; ancestor = ancestor.parentElement) {
          const ancestorHeight = heightOf(ancestor);
          if (ancestorHeight > this.minClampHeight && height - ancestorHeight > this.clampTolerance) {
            return ancestor;
          }
        }
        return null;
      };

      doc.querySelectorAll('#rhs img').forEach(image => {
        const height = heightOf(image);
        if (height < this.minClampHeight) {
          return;
        }
        const box = boxEscapedBy(image, height);
        if (box) {
          image.style.setProperty('max-height', heightOf(box) + 'px', 'important');
          image.style.setProperty('object-fit', 'contain', 'important');
        }
      });

      // A gallery taken out of the flow can still overrun its box as a whole even once
      // every image fits, by stacking them past the bottom of it.
      doc.querySelectorAll('#rhs div').forEach(gallery => {
        if (win.getComputedStyle(gallery).position !== 'absolute') {
          return;
        }
        const height = heightOf(gallery);
        if (height < this.minClampHeight) {
          return;
        }
        const box = boxEscapedBy(gallery, height);
        if (box) {
          gallery.style.setProperty('max-height', heightOf(box) + 'px', 'important');
          gallery.style.setProperty('overflow', 'hidden', 'important');
        }
      });
    },

    // Sizing measures the KP inside the iframe, which only has measurable dimensions
    // once the iframe itself has a layout box. The popup mounts its card as soon as the
    // HTML arrives - and serveHTML takes several seconds, so the dialog is often already
    // closed again (or still animating open) by then. Measured while hidden everything
    // comes out 0, the card is left collapsed to ~10px, and it never recovers because
    // the width is only ever measured once. That race is why the same KP renders for one
    // person and not another. So re-measure when the card actually becomes visible, and
    // stop as soon as it has a real width.
    /*
     * True while the frame is showing the empty document it started with rather than the
     * snapshot. #box is ours (addBackground), so it does not count as content.
     */
    isFrameBlank() {
      const doc = this.iframeDocument();
      if (!doc || !doc.body) {
        return true;
      }
      return !Array.from(doc.body.children).some(el => el.id !== 'box');
    },

    resizeWhenVisible() {
      if (this.visibilityObserver) {
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (!entries.some(entry => entry.isIntersecting) || !this.iframe() || !this.iframe().offsetParent) {
          return;
        }

        // The srcdoc navigation does not always commit - the attribute holds the whole
        // snapshot while the frame still shows the empty document it started with. Setting
        // srcdoc again re-runs the navigation, and its load event does the rest. Keep
        // watching until real content arrives; never treat a blank frame as done.
        if (this.isFrameBlank()) {
          if (this.htmlContent && this.reloadAttempts < this.maxReloadAttempts) {
            this.reloadAttempts++;
            this.iframe().setAttribute('srcdoc', this.htmlContent);
          }
          return;
        }

        // Only step in while there is no real measurement yet, so a good layout is never
        // disturbed. Measure the KP rather than the iframe: the iframe's width is fixed by the
        // stylesheet, so it reads wide even on a card whose measurement came out at nothing.
        if (this.cardWidth() > this.collapsedWidth) {
          this.stopWatchingVisibility();
          return;
        }

        if (this.termData && this.termData.hasKP) {
          this.layoutKp();
        }
        this.initZoom();

        if (this.cardWidth() > this.collapsedWidth) {
          this.trackCardHeight();
          this.stopWatchingVisibility();
        }
      });

      this.visibilityObserver.observe(this.iframe());
    },

    stopWatchingVisibility() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
      }
    },
    // DM-5609 Google hides the KP column on pages it considers too narrow, and the iframe
    // is only 25rem wide. The selector doing it differs from one scrape to the next - a
    // media query on `.vVVcqf` in some, the class `.O9jSzf` in others, and in the 8 Sep
    // 2026 scrape the rule `.rWfmBc .YNk70c .vVVcqf { display: none }`, with #rhs carrying
    // `vVVcqf` and div#main carrying `rWfmBc`. A hidden column measures 0x0, so
    // setCardWidth/initCardHeight shrink the iframe to ~10x35px and the card (and the KP
    // popup) renders blank even though the HTML holds a full KP.
    // So drive this off computed style rather than any class name: an inline !important
    // beats whatever rule Google reaches for next. Only ancestors of the KP are touched -
    // what is hidden inside it is forceElementsVisible's job.
    revealKpColumn() {
      const doc = this.iframeDocument();
      const rhs = doc.querySelector('#rhs');
      // Skip the center column: those are ancestors of the results, not of the KP.
      const sideKps = Array.from(
        doc.querySelectorAll('.kp-wholepage, .kp-wholepage-osrp, div[role="complementary"], [data-fb-rrhs], .knowledge-panel')
      ).filter(el => !el.closest('#center_col'));

      [rhs, ...sideKps].forEach(kp => this.unhideThroughAncestors(kp));
    },

    // Forces display on the element and every ancestor google has hidden. position:fixed cannot
    // override display:none, and forceElementsVisible() walks descendants only and keys off a
    // computed height of 0px, which a display:none element never reports because it has no box
    // at all - so without this the wrapper stayed hidden, measured 0x0, and the card came out
    // blank (10x59) on a day where the regular Timeline showed the KP fine. Measured on the
    // 18 Aug 2026 capture: 10x59 -> 385x537.
    //
    // Forcing `block` rather than restoring a per-element default is safe here because the KP is
    // given position:fixed straight after, which takes it out of any flex/grid ancestor's flow.
    // One shot is enough: google re-hides via a class, which an inline !important outranks.
    //
    // Do NOT break out of the loop at the first visible ancestor. getComputedStyle on a
    // descendant inside a display:none subtree still reports its own `display: block`, so a
    // visible-looking node tells you nothing about the rest of the chain - the walk has to
    // reach the root.
    unhideThroughAncestors(element) {
      const doc = this.iframeDocument();
      const win = this.iframe().contentWindow;
      for (let el = element; el && el !== doc.documentElement; el = el.parentElement) {
        // Never undo a removal: those are hidden on purpose, and an inline !important here
        // could not be taken back. Nothing the KP sits inside is ever in that set - the
        // removals pass skips the KP's own ancestors.
        if (this.hiddenByUs.has(el)) {
          continue;
        }
        if (win.getComputedStyle(el).display === 'none') {
          el.style.setProperty('display', 'block', 'important');
        }
      }
    },
    addBackground() {
      const box = document.createElement("div");
      box.id = "box";
      box.style.background = "white";
      box.style.width = '600px';
      box.style.height = '2000px';
      box.style['z-index'] = '99998';
      this.iframeDocument().body.prepend(box);
    },
    forceElementsVisible() {
      // Only fix height:0 for KP elements - check computed styles to catch both inline and CSS-applied heights
      const fixHeightZero = () => {
        const kpSelectors = '#rhs *, .kp-wholepage *, .kp-wholepage-osrp *, [role="complementary"] *, [data-fb-rrhs] *';
        this.iframeDocument().querySelectorAll(kpSelectors).forEach(el => {
          const computedStyle = window.getComputedStyle(el);
          if (computedStyle.height === '0px' || el.style.height === '0' || el.style.height === '0px') {
            el.style.setProperty('height', 'auto', 'important');
            el.style.setProperty('display', 'block', 'important');
          }
        });
      };
      
      // Run immediately and set up observer for dynamic changes
      fixHeightZero();
      
      const observer = new MutationObserver(() => {
        fixHeightZero();
      });
      
      observer.observe(this.iframeDocument().body, { 
        attributes: true, 
        attributeFilter: ['style'],
        childList: true, 
        subtree: true 
      });
      this.observers.push(observer);
    },
    initZoom() {
      this.setZoom(this.zoomSelection, true);
    },
    setZoom(selection, init = false) {
      const zoom = this.shouldZoom && selection == 'medium-view' ? 0.6 : 1;

      this.iframeDocument().body.style.zoom = zoom;
      this.zoom = zoom;
      this.setCardWidth();
      if (init) {
        this.initCardHeight();
      } else if (this.shouldZoom) {
        const fixZoom = selection == 'medium-view' ? 0.6 : 1 / 0.6;
        this.iframe().height *= fixZoom;
      }
      this.$emit("resize", this.iframeStyle().getPropertyValue('width'), this.iframeStyle().getPropertyValue('height'));
    },
    // Sizes the CLIPPING BOX, not the iframe. Writing the card width onto the iframe is what
    // this used to do, and it undid the wide render: the page inside is live, and at a card's
    // width Google's own layout code moves the knowledge panel out of #rhs into the main
    // column, so everything keyed off #rhs (the pin, the height) found an empty element and the
    // card came up blank. The iframe keeps the wide width from the stylesheet; the panel is
    // pinned to its top-left corner, so clipping to the card width shows the panel alone.
    setCardWidth() {
      const clipBox = this.cardBox();
      if (!clipBox) {
        return;
      }
      if (this.fillWidth) {
        this.fillCardBox(clipBox);
        return;
      }

      // Drop the width an earlier pass set, so this measures the box at its stylesheet width.
      clipBox.style.removeProperty("width");

      const width = this.cardWidth();
      if (width > 0) {
        clipBox.style.width = width + "px";
        // A vertical scrollbar (the dialog puts one on this box) is taken out of the width we
        // just set, which clips the right edge of the panel. Give it back.
        const chrome = clipBox.offsetWidth - clipBox.clientWidth;
        if (chrome > 0) {
          clipBox.style.width = width + chrome + "px";
        }
      }
    },
    // DM-11385: the KP popup's panel takes the popup's whole width. The iframe is sized with it:
    // the captured page lays itself out against the iframe's width (a panel wider than its
    // iframe crops), and Google's panel reflows to fit.
    fillCardBox(clipBox) {
      clipBox.style.width = 'auto';
      const width = clipBox.clientWidth; // without the box's scrollbar
      if (width <= 0) {
        return;
      }
      this.iframe().style.width = width + 'px';
      const kp = this.kpWrapper();
      if (kp && kp.id !== ASSEMBLED_PANEL_ID) {
        kp.style.setProperty('width', width - this.bufferWidth + 'px', 'important');
      }
      this.fitAioSources();
    },
    // The sources list sits inside Google's section, and Google's page clips short of the
    // iframe's edge (`.main` is overflow-x: clip), so a full-width list loses its right edge: end
    // it inside the narrowest box that clips it.
    fitAioSources() {
      const mount = this.aioMount;
      if (!mount) {
        return;
      }
      mount.style.removeProperty('max-width');
      const doc = this.iframeDocument();
      const win = this.iframe().contentWindow;
      let right = doc.documentElement.clientWidth;
      for (let el = mount.parentElement; el && el !== doc.documentElement; el = el.parentElement) {
        if (win.getComputedStyle(el).overflowX !== 'visible') {
          right = Math.min(right, el.getBoundingClientRect().right);
        }
      }
      const room = right - mount.getBoundingClientRect().left - this.bufferWidth / 2;
      if (room > 0) {
        mount.style.setProperty('max-width', room + 'px', 'important');
      }
    },
    initCardHeight() {
      const kpWrapper = this.kpWrapper() || this.kpElements()[0];

      if (!kpWrapper) return;

      // Drop the height a previous pass pinned before measuring. The wrapper is
      // position:fixed, so the height:100% set at the end of this method ties it to the
      // iframe's current height - measure once while the card is small and the two hold
      // each other down there, and the card can never grow back.
      kpWrapper.style.removeProperty('height');

      const kpSectionComputed = window.getComputedStyle(kpWrapper);
      
      this.iframe().height =
        kpWrapper.scrollHeight +
        parseFloat(kpSectionComputed["marginTop"]) +
        parseFloat(kpSectionComputed["marginBottom"]) +
        parseFloat(kpSectionComputed["paddingTop"]) +
        parseFloat(kpSectionComputed["paddingBottom"]) +
        this.bufferHeight;

      this.iframe().height *= this.zoom;

      // Only for the panels we did not build. On an assembled panel this feeds straight back
      // into the measurement above, because the panel is then sized from the iframe it sizes.
      if (kpWrapper.id !== ASSEMBLED_PANEL_ID) {
        kpWrapper.style.height = '100%';
      }
    },
    // The panel settles late - images load, and the observers set up above keep unhiding
    // sections Google had collapsed. Follow it rather than guessing when it has stopped.
    // Grow-only, so a transient shrink cannot start it oscillating against its own iframe.
    trackCardHeight() {
      // The termData watcher re-runs processKpHtml, which schedules this again. Tear down the
      // previous observer first: it is still watching an element from the old srcdoc document
      // and would keep calling initCardHeight(), sizing the new card from a stale element's
      // scrollHeight. Disconnect before the guard below, so a run that bails still clears it -
      // beforeUnmount() only ever disconnects the last one.
      if (this.cardHeightObserver) {
        this.cardHeightObserver.disconnect();
        this.cardHeightObserver = null;
      }

      const kpWrapper = this.kpWrapper() || this.kpElements()[0];
      if (!kpWrapper || typeof ResizeObserver === 'undefined') {
        return;
      }

      let scheduled = false;
      let adjustments = 0;

      const observer = new ResizeObserver(() => {
        if (scheduled || adjustments >= 20) {
          return;
        }
        scheduled = true;
        // Resize on a later tick, never inside the callback. Growing the iframe resizes the very
        // element being observed, and doing that during delivery is what raises "ResizeObserver
        // loop completed with undelivered notifications". The threshold and the cap keep a panel
        // that reflows as it grows from trading heights with its own iframe indefinitely.
        setTimeout(() => {
          scheduled = false;
          if (kpWrapper.scrollHeight - Number(this.iframe().height) > 8) {
            adjustments += 1;
            this.initCardHeight();
          }
        }, 150);
      });
      observer.observe(kpWrapper);
      this.cardHeightObserver = observer;
    },
    // DM-5314 Google adds classes through a function based on window size. Those classes make display = none. This removes those classes continuously. 
    // For now, this works. If we see that google changes classes too often, then we'll have to attack it from the script tags themselves.  
    removeClassRepeatedly(iframeDocument) {
      if (!iframeDocument) return;

      iframeDocument.querySelectorAll('.O9jSzf').forEach(el => {
        el.classList.remove('O9jSzf');
      });

      const observer = new MutationObserver(() => {
        iframeDocument.querySelectorAll('.O9jSzf').forEach(el => {
            el.classList.remove('O9jSzf');
        });
      });

      observer.observe(iframeDocument.body, { childList: true, subtree: true });
      this.observers.push(observer);
    },
    // DM-5314 Google had a javscript function running that would reset display to none (based on the window width) if we manually set display to '' or block.
    // As a solution, I set an observer and anytime display is changed back to none, it is automatically set to 'block'
    // For now, this works. If we see that google changes classes too often, then we'll have to attack it from the script tags themselves.
    showHiddenElementsRepeatedly(iframeDocument) {
      if (!iframeDocument) return;

      const resetDisplay = () => {
        iframeDocument.querySelectorAll('.z4oRIf .lDX8Cc .W0urI.EVeTFb').forEach(el => {
          const computedDisplay = window.getComputedStyle(el).display;
          if (computedDisplay === 'none') {
            el.style.setProperty('display', 'block', 'important');
          }
        });
      };

      resetDisplay();

      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === "attributes" || mutation.type === "childList") {
            resetDisplay();
          }
        });
      });
      observer.observe(iframeDocument.body, { childList: true, subtree: true, attributes: true });
      this.observers.push(observer);
    },
    
    updateHighlighting() {
      let styles = '';
      for (const [itemId, color] of Object.entries(this.highlightedKP[this.cardMeta.id] || {})) {
        styles += `[data-attrid*='${itemId}'] { background-color: ${color} !important; }\n`;
      }
      HighlightUtil.setStyles(styles, this.iframeDocument());
    },
  },
  mounted() {
    if (this.termData) {
      this.processKpHtml();
    }
  },
  beforeUnmount() {
    this.observers.splice(0).forEach(observer => observer.disconnect());
    if (this.cardHeightObserver) {
      this.cardHeightObserver.disconnect();
      this.cardHeightObserver = null;
    }
    this.stopWatchingVisibility();
  },
  watch: {
    termData: {
      deep: true,
      handler: function () {
        if (this.termData) {
          this.processKpHtml();
        }
      },
    },
    aioverview() {
      this.remountAioSources();
    },
    showNoAio() {
      this.remountAioSources();
    },
    hoverFocusItem: {
      deep: true,
      handler: function () {
        const hoverFocusElements = this.iframeDocument().querySelectorAll(
          `[data-attrid*='${this.hoverFocusItem.value}']`
        );
        hoverFocusElements.forEach((hoverFocusElement) => {
          hoverFocusElement.style.borderColor = this.hoverFocusItem.isHoverFocus
            ? "black"
            : "transparent";
        });
      },
    },
    highlightedKP: {
      deep: true,
      handler: function () {
        this.updateHighlighting();
      },
    },
    zoomSelection(currentSelection) {
      if (this.shouldZoom) {
        this.setZoom(currentSelection);
      }
    },
    loadingHtmlComplete(value) {
      this.isLoading = value;
    }
  },
};
</script>

<style lang="scss" scoped>
.card-wrapper {
  position: relative;
  background: var(--timeline-card-bg);
  margin: 0px calc(var(--timeline-between-cards-spacing) / 2);
  border-radius: var(--border-radius-card);

  .card-content {
    .unhide-text {
      position: absolute;
      top: 200px;
      width: 100%;
      z-index: 999;
      color: white;
      text-align: center;
      height: 70px;
    }

    .unhide-overlay {
      position: absolute;
      width: 100%;
      height: calc(100% - 115px);
      background-color: #cacaca;
      z-index: 998;
      opacity: 0.6;
    }

    .spinner-border {
      position: absolute;
      font-size: 26px;
      width: 5rem;
      height: 5rem;
      color: var(--fb-blue-70);
      top: 30%;
      left: 40%;

    }
  }

  // The iframe is rendered wide and clipped by .card-content, rather than being sized to
  // the card. See --timeline-card-render-width-kp for why: the stored page lays itself
  // out against the iframe width, and a narrow one relocates the panel out of #rhs.
  // The panel is pinned to the iframe's top-left (layoutKp), so clipping to the card's
  // width shows exactly the panel and nothing else.
  &.regular-view {
    .card-content {
      margin: 0 var(--timeline-result-margin-regular);
      width: var(--timeline-card-width-kp-regular);
      overflow-x: hidden;
    }
  }

  &.medium-view {

    .card-content {
      margin: 0 var(--timeline-result-margin-medium);
      width: var(--timeline-card-width-kp-medium);
      overflow-x: hidden;
    }
  }
}
</style>


